import { useState, useEffect, useCallback, useRef } from "react";

interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Simple fetch wrapper with loading/error states.
 * Uses credentials: 'include' for cookie auth.
 *
 * Only the newest request may write state: SSE-driven refetches abort the prior
 * one, and letting it clear `loading` renders pages as "not found" with no data.
 */
export function useApi<T>(url: string): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const generationRef = useRef(0);
  const dataRef = useRef<T | null>(null);
  const urlRef = useRef<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!url) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const generation = ++generationRef.current;
    const isStale = () => generationRef.current !== generation;

    if (urlRef.current !== url) {
      urlRef.current = url;
      dataRef.current = null;
      setData(null);
    }
    if (dataRef.current === null) {
      setLoading(true);
    }
    setError(null);

    try {
      const res = await fetch(url, {
        credentials: "include",
        signal: controller.signal,
      });
      if (isStale()) return;

      if (res.status === 401) {
        window.location.href = "/auth/login";
        return;
      }

      if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}`);
      }

      const json: T = await res.json();
      if (isStale()) return;
      dataRef.current = json;
      setData(json);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      if (isStale()) return;
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  }, [url]);

  useEffect(() => {
    if (!url) {
      setLoading(false);
      return;
    }
    fetchData();
    return () => {
      // Fence any in-flight request on unmount/url change so its rejection
      // cannot mutate state after this component stopped caring.
      generationRef.current += 1;
      abortRef.current?.abort();
    };
  }, [fetchData, url]);

  return { data, loading, error, refetch: fetchData };
}
